/** Modèle de ligne du registre des parties intéressées (ISO 9001:4.2 enrichi). */

import type { DocumentValueDto } from '@app/contracts';

export interface RegistryRowSite {
  id: string;
  name: string;
  city?: string;
  country?: string;
}

export interface RegistryRowModule {
  id: string;
  name: string;
  code: string;
}

export interface RegistryRow {
  id: string | number | null;
  designation: string;
  categorie: string;
  besoinsEtAttentes: string;
  exigences: string;
  impact: string;
  modalitesPriseEnCompte: string;
  pilote: string;
  dateDerniereRevue: string;
  dateProchaineRevue: string;
  commentaires: string;
  sites: RegistryRowSite[];
  modules: RegistryRowModule[];
  dateCreated: string;
  dateUpdated: string;
}

export interface RegistryRowSerialized {
  designation: string;
  categorie: string;
  besoinsEtAttentes: string;
  exigences: string;
  impact: string;
  modalitesPriseEnCompte: string;
  pilote: string;
  dateDerniereRevue: string;
  dateProchaineRevue: string;
  commentaires: string;
}

export const REGISTRY_CATEGORIES = [
  { value: '', label: '— Choisir —' },
  { value: 'interne', label: 'Interne' },
  { value: 'externe', label: 'Externe' },
  { value: 'regulateur', label: 'Régulateur / légal' },
  { value: 'autre', label: 'Autre' },
] as const;

export const REGISTRY_IMPACTS = [
  { value: '', label: '— Choisir —' },
  { value: 'faible', label: 'Faible' },
  { value: 'moyen', label: 'Moyen' },
  { value: 'eleve', label: 'Élevé' },
] as const;

export function formatCategorieLabel(value: string): string {
  const f = REGISTRY_CATEGORIES.find((c) => c.value === value);
  return f ? f.label : value || '—';
}

export function formatImpactLabel(value: string): string {
  const f = REGISTRY_IMPACTS.find((c) => c.value === value);
  return f ? f.label : value || '—';
}

function dtoSitesToRegistry(
  sites: Array<{ id: string; name: string; city?: string | null; country?: string | null }>,
): RegistryRowSite[] {
  return sites.map((s) => ({
    id: s.id,
    name: s.name,
    city: s.city ?? undefined,
    country: s.country ?? undefined,
  }));
}

function dtoModulesToRegistry(
  modules: Array<{ id: string; code: string; name: string }>,
): RegistryRowModule[] {
  return modules.map((m) => ({
    id: m.id,
    name: m.name,
    code: m.code,
  }));
}

/** Périmètre BDD prioritaire si non vide, sinon champs éventuels dans le JSON (rétrocompat). */
export function mergeRegistryPerimeterFromValue(
  raw: Record<string, unknown>,
  value: Pick<DocumentValueDto, 'sites' | 'modules'>,
): { sites: RegistryRowSite[]; modules: RegistryRowModule[] } {
  const fromApiSites = dtoSitesToRegistry(value.sites ?? []);
  const fromApiMods = dtoModulesToRegistry(value.modules ?? []);
  const useApi = fromApiSites.length > 0 || fromApiMods.length > 0;
  return {
    sites: useApi ? fromApiSites : normalizeRowSites(raw.sites),
    modules: useApi ? fromApiMods : normalizeRowModules(raw.modules),
  };
}

/** Fusionne le périmètre BDD (par `clientKey`) pour un élément de matrice SWOT/PESTEL. */
export function mergeMatrixItemPerimeterFromDto<
  T extends { clientKey: string; sites: RegistryRowSite[]; modules: RegistryRowModule[] },
>(
  item: T,
  byRef: Map<string, { sites: DocumentValueDto['sites']; modules: DocumentValueDto['modules'] }>,
): T {
  const p = byRef.get(item.clientKey);
  if (p && (p.sites.length > 0 || p.modules.length > 0)) {
    return {
      ...item,
      sites: dtoSitesToRegistry(p.sites),
      modules: dtoModulesToRegistry(p.modules),
    };
  }
  return item;
}

export function normalizeRegistryRow(
  raw: Record<string, unknown>,
  value?: Pick<DocumentValueDto, 'sites' | 'modules'>,
): RegistryRow {
  if (!raw || typeof raw !== 'object') {
    return createEmptyRow();
  }

  const designation = (raw.designation as string) || (raw.partieInteressee as string) || '';

  let besoinsEtAttentes = (raw.besoinsEtAttentes as string) || '';
  if (!besoinsEtAttentes && (raw.attentes || raw.exigences)) {
    const parts = [raw.attentes, raw.exigences].filter(Boolean) as string[];
    besoinsEtAttentes = parts.join('\n\n');
  }

  const { sites, modules } = value
    ? mergeRegistryPerimeterFromValue(raw, value)
    : { sites: normalizeRowSites(raw.sites), modules: normalizeRowModules(raw.modules) };

  return {
    id: (raw.id as string | number | null) ?? null,
    designation,
    categorie: (raw.categorie as string) || '',
    besoinsEtAttentes,
    exigences: (raw.exigences as string) || '',
    impact: (raw.impact as string) || '',
    modalitesPriseEnCompte: (raw.modalitesPriseEnCompte as string) || '',
    pilote: (raw.pilote as string) || '',
    dateDerniereRevue: (raw.dateDerniereRevue as string) || '',
    dateProchaineRevue: (raw.dateProchaineRevue as string) || '',
    commentaires: (raw.commentaires as string) || '',
    sites,
    modules,
    dateCreated: (raw.dateCreated as string) || new Date().toISOString(),
    dateUpdated: (raw.dateUpdated as string) || new Date().toISOString(),
  };
}

/** Normalise un tableau `sites` stocké dans le JSON d’une valeur de document. */
export function normalizeRowSites(v: unknown): RegistryRowSite[] {
  if (!Array.isArray(v)) return [];
  const out: RegistryRowSite[] = [];
  for (const x of v) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const id = String(o.id ?? '');
    const name = String(o.name ?? '');
    if (!id || !name) continue;
    const city = o.city != null && o.city !== '' ? String(o.city) : undefined;
    const country = o.country != null && o.country !== '' ? String(o.country) : undefined;
    out.push({ id, name, city, country });
  }
  return out;
}

/** Normalise un tableau `modules` stocké dans le JSON d’une valeur de document. */
export function normalizeRowModules(v: unknown): RegistryRowModule[] {
  if (!Array.isArray(v)) return [];
  const out: RegistryRowModule[] = [];
  for (const x of v) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const id = String(o.id ?? '');
    const name = String(o.name ?? '');
    const code = String(o.code ?? '');
    if (!id || !name) continue;
    out.push({ id, name, code });
  }
  return out;
}

export function createEmptyRow(): RegistryRow {
  const now = new Date().toISOString();
  return {
    id: `${Date.now()}${Math.floor(Math.random() * 1_000_000)}`,
    designation: '',
    categorie: '',
    besoinsEtAttentes: '',
    exigences: '',
    impact: '',
    modalitesPriseEnCompte: '',
    pilote: '',
    dateDerniereRevue: '',
    dateProchaineRevue: '',
    commentaires: '',
    sites: [],
    modules: [],
    dateCreated: now,
    dateUpdated: now,
  };
}

export function rowToSerializedData(row: RegistryRow): RegistryRowSerialized {
  return {
    designation: row.designation || '',
    categorie: row.categorie || '',
    besoinsEtAttentes: row.besoinsEtAttentes || '',
    exigences: row.exigences || '',
    impact: row.impact || '',
    modalitesPriseEnCompte: row.modalitesPriseEnCompte || '',
    pilote: row.pilote || '',
    dateDerniereRevue: row.dateDerniereRevue || '',
    dateProchaineRevue: row.dateProchaineRevue || '',
    commentaires: row.commentaires || '',
  };
}

export function summarizePerimetre(row: RegistryRow): string {
  const ns = row.sites?.length || 0;
  const nm = row.modules?.length || 0;
  if (ns === 0 && nm === 0) return '—';
  return `${ns} site(s), ${nm} module(s)`;
}

export function validateRowStrict(
  row: RegistryRow,
  options?: { requireSites?: boolean; skipContentRules?: boolean },
): string[] {
  const err: string[] = [];
  if (!options?.skipContentRules) {
    if (!String(row.designation || '').trim()) {
      err.push('La désignation de la partie intéressée est obligatoire.');
    }
    const hasBesoins = String(row.besoinsEtAttentes || '').trim();
    const hasExig = String(row.exigences || '').trim();
    if (!hasBesoins && !hasExig) {
      err.push('Renseignez les besoins & attentes et/ou les exigences applicables.');
    }
  }
  if (options?.requireSites && (!row.sites?.length || row.sites.length < 1)) {
    err.push('Sélectionnez au moins un site concerné.');
  }
  return err;
}
